import { Injectable } from '@angular/core';
import { ChatButtonId, OmniAnalyticsService } from '@cigna/omni/shared';
import { ConversationDTO, ConversationSummaryDTO } from '@cigna/vampire-dto';
import { select, Store } from '@ngrx/store';
import { publishReplay, refCount } from 'rxjs/operators';
import { conversationsActions } from './conversations.actions';
import { conversationsQuery } from './conversations.selectors';

@Injectable()
export class OmniConversationsFacade {
  public activeChatProvider$ = this.store$.pipe(
    select(conversationsQuery.getActiveChatProvider),
  );
  public activityDebounceTime$ = this.store$.pipe(
    select(conversationsQuery.getActivityDebounceTime),
  );

  public conversationsEntities$ = this.store$.pipe(
    select(conversationsQuery.selectConversations),
  );

  public allConversations$ = this.store$.pipe(
    select(conversationsQuery.selectAllConversations),
  );

  public activeLiveConversation$ = this.store$.pipe(
    select(conversationsQuery.selectActiveLiveConversation),
  );

  public conversationCount$ = this.store$.pipe(
    select(conversationsQuery.selectConversationCount),
  );
  public conversation$ = this.store$.pipe(
    select(conversationsQuery.selectConversation),
  );
  public conversationEntities$ = this.store$.pipe(
    select(conversationsQuery.selectConversations),
  );
  public conversationId$ = this.store$.pipe(
    select(conversationsQuery.selectSelectedConversationId),
  );
  public isMyConversationsFeatured$ = this.store$.pipe(
    select(conversationsQuery.selectIsMyConversationsFeatured),
    publishReplay(1),
    refCount(),
  );
  public notificationCount$ = this.store$.pipe(
    select(conversationsQuery.selectNotificationCount),
  );
  public recentActiveOrUnreadConversation$ = this.store$.pipe(
    select(conversationsQuery.selectRecentActiveOrUnreadConversation),
  );

  public unreadOpenDmConversations$ = this.store$.pipe(
    select(conversationsQuery.selectUnreadOpenDMConversations),
  );

  public expanderBubble$ = this.store$.pipe(
    select(conversationsQuery.getExpander),
  );

  public closeOrExitFlag$ = this.store$.pipe(
    select(conversationsQuery.getCloseOrExitFlag),
  );

  public closeSplashScreen$ = this.store$.pipe(
    select(conversationsQuery.getCloseSplashScreen),
  );

  public canAutoOpenConversation$ = this.store$.pipe(
    select(conversationsQuery.autoOpenConversation),
  );

  public isUpdatingConversation$ = this.store$.pipe(
    select(conversationsQuery.getIsUpdatingConversation),
  );

  public isAiBot$ = this.store$.pipe(select(conversationsQuery.getIsAiBot));

  public existingConversationId$ = this.store$.pipe(
    select(conversationsQuery.getExistingConversationId),
  );

  public shouldFocusOnHeader$ = this.store$.pipe(
    select(conversationsQuery.getShouldFocusOnHeader),
  );

  public conversationMetadata$ = this.store$.pipe(
    select(conversationsQuery.getConversationMetadata),
  );

  constructor(
    private store$: Store<any>,
    private analytics: OmniAnalyticsService,
  ) {}

  public createConversation(
    type: ConversationDTO['type'],
    context: ConversationDTO['context'],
    message?: string,
  ): void {
    this.store$.dispatch(
      new conversationsActions.CreateConversation({ type, context, message }),
    );
  }

  public chatBubbleClick(
    buttonId: ChatButtonId,
    buttonText: string,
    closed = false,
  ) {
    this.store$.dispatch(
      new conversationsActions.ChatBubbleClick(buttonId, buttonText, closed),
    );
  }

  public agentJoinChat() {
    this.store$.dispatch(new conversationsActions.AgentJoinChat());
  }

  public endConversation(conversationId: string): void {
    this.setConversationId('');
    this.store$.dispatch(
      new conversationsActions.EndConversation(conversationId),
    );
  }

  public backToPrevious(
    convId: string,
    context: ConversationDTO['context'],
  ): void {
    this.store$.dispatch(
      new conversationsActions.BackToPrevious({ convId, context }),
    );
  }

  public backToStart(): void {
    this.store$.dispatch(new conversationsActions.BackToStart());
  }

  public terminateConversation(
    userAction: 'logout' | 'inactive',
    logoutUrl: string,
  ): void {
    this.store$.dispatch(
      new conversationsActions.TerminateConversation(userAction, logoutUrl),
    );
  }

  public loadConversations(): void {
    this.store$.dispatch(new conversationsActions.LoadConversations());
  }

  public getConversationHistory(): void {
    this.store$.dispatch(new conversationsActions.GetConversationHistory());
  }

  public loadConversationsSuccess(
    conversations: ConversationDTO[] | ConversationSummaryDTO[],
  ): void {
    this.store$.dispatch(
      new conversationsActions.LoadConversationsSuccess(conversations),
    );
  }

  public loadConversationsFailure(error: any): void {
    this.store$.dispatch(
      new conversationsActions.LoadConversationsFailure(error),
    );
  }

  public messageViewed(messageId: string): void {
    this.store$.dispatch(new conversationsActions.MessageViewed(messageId));
  }

  public onKeyUp() {
    this.store$.dispatch(new conversationsActions.OnKeyUp());
  }

  public postCta(
    sourceMessageId: string,
    value: string,
    topicId: string,
    label: string,
    _id: string,
    primaryTopicName?: string,
  ) {
    this.analytics.track('chatTopicClick', {
      topicId,
      topicLabel: label,
      primaryTopicName,
    });
    this.store$.dispatch(
      new conversationsActions.PostCta({ sourceMessageId, value, _id }),
    );
  }

  public postMessage(message: string) {
    this.store$.dispatch(new conversationsActions.PostMessage(message));
  }

  public setConversationId(conversationId: string): void {
    this.store$.dispatch(
      new conversationsActions.SetSelectedConversationId(conversationId),
    );
  }

  public setActivityDebounceTime(debounce: number): void {
    this.store$.dispatch(
      new conversationsActions.SetActivityDebounceTime(debounce),
    );
  }

  public setActiveChatProvider(provider: string): void {
    this.store$.dispatch(
      new conversationsActions.SetActiveChatProvider(provider),
    );
  }

  public getActiveConversation(): void {
    this.store$.dispatch(new conversationsActions.GetActiveConversation());
  }

  public transferConversation(
    conversationId: string,
    type: ConversationDTO['type'],
    initial: string,
  ): void {
    this.store$.dispatch(
      new conversationsActions.TransferConversation({
        conversationId,
        type,
        initial,
      }),
    );
  }

  public setExpanderBubble(isHide: boolean): void {
    this.store$.dispatch(new conversationsActions.SetExpanderBubble(isHide));
  }

  public setCloseOrExitFlag(isClosed: boolean): void {
    this.store$.dispatch(new conversationsActions.SetCloseOrExitFlag(isClosed));
  }

  public setCloseSplashScreen(closeSplashScreen: boolean): void {
    this.store$.dispatch(
      new conversationsActions.SetCloseSplashScreen(closeSplashScreen),
    );
  }

  public setExistingConversationId(existingConversationId: string): void {
    this.store$.dispatch(
      new conversationsActions.SetExistingConversationId(
        existingConversationId,
      ),
    );
  }

  public setHeaderFocus(shouldFocus: boolean) {
    this.store$.dispatch(new conversationsActions.SetHeaderFocus(shouldFocus));
  }

  public updateConversationMetadata(data: any) {
    this.store$.dispatch(
      new conversationsActions.UpdateConversationMetadata(data),
    );
  }
}
